import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { Download, Cloud, CheckCircle, Pen, Camera, AlertCircle, AlertTriangle, Copy, Check, Link as LinkIcon } from 'lucide-react'
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'
import { SignatureCanvas } from '../components/SignatureCanvas'
import { SignatureUpload } from '../components/SignatureUpload'
import { fetchTemplate, uploadToR2, verifyTurnstile, recordStat } from '../utils/api'
import { InsurerAutocomplete } from '../components/InsurerAutocomplete'
import { decodeHashData, generatePrivacySafeLink } from '../utils/hashData'
import { generatePdf, downloadPdf } from '../utils/pdfGenerator'
import { processSignatureImage, type ProcessedSignature } from '../utils/signatureProcessor'
import type { Template, FormData, SignatureData } from '../types'

const TURNSTILE_SITE_KEY = '0x4AAAAAACW2xKcOgr7vwU8z'
const TURNSTILE_REFRESH_INTERVAL = 270000 // Refresh 30 seconds before 5-minute expiry

type SignatureMode = 'draw' | 'photo'

export function SignPage() {
  const { templateId } = useParams<{ templateId: string }>()
  const location = useLocation()
  const turnstileRef = useRef<TurnstileInstance>(null)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [template, setTemplate] = useState<Template | null>(null)
  const [formData, setFormData] = useState<FormData>({})
  const [signature, setSignature] = useState<SignatureData | null>(null)
  const [signatureMode, setSignatureMode] = useState<SignatureMode>('draw')
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [showCloudWarning, setShowCloudWarning] = useState(false)
  const [cloudUrl, setCloudUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [generatedLink, setGeneratedLink] = useState<string | null>(null)
  const [linkCopied, setLinkCopied] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)

  // Auto-refresh Turnstile token before it expires
  const scheduleTokenRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
    }
    refreshTimerRef.current = setTimeout(() => {
      turnstileRef.current?.reset()
    }, TURNSTILE_REFRESH_INTERVAL)
  }, [])

  const handleTurnstileSuccess = useCallback((token: string) => {
    setTurnstileToken(token)
    scheduleTokenRefresh()
  }, [scheduleTokenRefresh])

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null)
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
    }
    // Auto-refresh on expire
    turnstileRef.current?.reset()
  }, [])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!templateId) return

    const hash = location.hash
    if (hash.startsWith('#data=')) {
      const decoded = decodeHashData(hash)
      if (decoded?.data) {
        setFormData(decoded.data)
      }
    }

    fetchTemplate(templateId)
      .then(setTemplate)
      .catch(() => setError('Erro ao carregar o modelo'))
      .finally(() => setLoading(false))
  }, [templateId, location.hash])

  const handleInputChange = useCallback((key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }))
  }, [])

  const handleCanvasSignature = useCallback(async (dataUrl: string | null) => {
    if (!dataUrl) {
      setSignature(null)
      return
    }
    try {
      const processed = await processSignatureImage(dataUrl, {
        maxWidth: 400,
        maxHeight: 150,
        luminanceThreshold: 250,
      })
      setSignature({
        dataUrl: processed.dataUrl,
        width: processed.width,
        height: processed.height,
      })
    } catch {
      setSignature({
        dataUrl,
        width: 350,
        height: 150,
      })
    }
  }, [])

  const handlePhotoSignature = useCallback((processed: ProcessedSignature | null) => {
    if (!processed) {
      setSignature(null)
      return
    }
    setSignature({
      dataUrl: processed.dataUrl,
      width: processed.width,
      height: processed.height,
    })
  }, [])

  const handleGeneratePdf = useCallback(async () => {
    if (!template || !turnstileToken) {
      setError('Por favor, complete a verificação de segurança.')
      return
    }

    setGenerating(true)
    setError(null)

    try {
      // Verify turnstile first
      const isValid = await verifyTurnstile(turnstileToken, 'generate_pdf')
      if (!isValid) {
        setError('Verificação de segurança falhou. Por favor, tente novamente.')
        turnstileRef.current?.reset()
        setTurnstileToken(null)
        return
      }

      const pdfBytes = await generatePdf(template.content, formData, signature)
      const filename = `${template.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`
      downloadPdf(pdfBytes, filename)
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
      
      // Record stat (fire and forget)
      recordStat('pdf_generated', template.id)
      
      // Reset turnstile for next action
      turnstileRef.current?.reset()
      setTurnstileToken(null)
    } catch (err) {
      console.error('PDF generation error:', err)
      setError('Erro ao gerar o PDF. Por favor, tente novamente.')
    } finally {
      setGenerating(false)
    }
  }, [template, formData, signature, turnstileToken])

  const isFormValid = useCallback(() => {
    if (!template) return false
    const requiredFields = template.fields.filter((f) => f.required)
    return requiredFields.every((field) => formData[field.name]?.trim())
  }, [template, formData])

  const handleCloudSave = useCallback(async () => {
    if (!template || !turnstileToken) {
      setError('Por favor, complete a verificação de segurança.')
      return
    }

    setUploading(true)
    setError(null)

    try {
      // Verify turnstile first
      const isValid = await verifyTurnstile(turnstileToken, 'upload_cloud')
      if (!isValid) {
        setError('Verificação de segurança falhou. Por favor, tente novamente.')
        turnstileRef.current?.reset()
        setTurnstileToken(null)
        return
      }

      const pdfBytes = await generatePdf(template.content, formData, signature)
      const filename = `${template.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`
      const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' })
      
      const result = await uploadToR2(blob, filename)
      const fullUrl = `${window.location.origin}${result.url}`
      setCloudUrl(fullUrl)
      setShowCloudWarning(false)
      
      // Record stat (fire and forget)
      recordStat('cloud_upload', template.id)
      
      // Reset turnstile for next action
      turnstileRef.current?.reset()
      setTurnstileToken(null)
    } catch (err) {
      console.error('Upload error:', err)
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido'
      setError(`Erro ao guardar na cloud: ${errorMessage}`)
    } finally {
      setUploading(false)
    }
  }, [template, formData, signature, turnstileToken])

  const handleCopyUrl = async () => {
    if (!cloudUrl) return
    try {
      await navigator.clipboard.writeText(cloudUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      alert('Erro ao copiar o link')
    }
  }

  const handleGenerateLink = async () => {
    if (!template || !templateId) return
    
    if (!turnstileToken) {
      setError('Por favor, complete a verificação de segurança.')
      return
    }

    try {
      // Verify turnstile first
      const isValid = await verifyTurnstile(turnstileToken, 'generate_link')
      if (!isValid) {
        setError('Verificação de segurança falhou. Por favor, tente novamente.')
        turnstileRef.current?.reset()
        setTurnstileToken(null)
        return
      }

      // Filter out empty values from formData
      const filledData: FormData = {}
      Object.entries(formData).forEach(([key, value]) => {
        if (value && value.trim()) {
          filledData[key] = value
        }
      })
      
      const link = generatePrivacySafeLink(window.location.origin, templateId, filledData)
      setGeneratedLink(link)
      setShowLinkModal(true)
      setLinkCopied(false)
      
      // Record stat (fire and forget)
      recordStat('url_generated', template.id)
      
      // Reset turnstile for next action
      turnstileRef.current?.reset()
      setTurnstileToken(null)
    } catch {
      setError('Erro ao gerar o link. Por favor, tente novamente.')
    }
  }

  const handleCopyLink = async () => {
    if (!generatedLink) return
    try {
      await navigator.clipboard.writeText(generatedLink)
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2000)
    } catch {
      alert('Erro ao copiar o link')
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (error && !template) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    )
  }

  if (!template) return null

  return (
    <div className="flex-1 py-6 px-4">
      <div className="max-w-lg mx-auto space-y-6">
        <div className="text-center">
          <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-sm font-medium rounded-full mb-2">
            {template.category}
          </span>
          <h1 className="text-2xl font-bold text-gray-900">{template.name}</h1>
          {template.description && (
            <p className="text-gray-500 mt-1">{template.description}</p>
          )}
        </div>

        <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
          <div className="card p-6 space-y-4">
            <h2 className="font-semibold text-gray-900">Preencha os Dados</h2>
            {template.fields.map((field) => (
              <div key={field.name}>
                <label htmlFor={field.name} className="label">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {field.type === 'textarea' ? (
                  <textarea
                    id={field.name}
                    value={formData[field.name] || ''}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    className="input min-h-[100px] resize-none"
                    placeholder={field.placeholder}
                    required={field.required}
                  />
                ) : field.type === 'date' ? (
                  <input
                    type="date"
                    id={field.name}
                    value={formData[field.name] || ''}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    className="input"
                    required={field.required}
                  />
                ) : field.name.toLowerCase() === 'seguradora' ? (
                  <InsurerAutocomplete
                    id={field.name}
                    value={formData[field.name] || ''}
                    onChange={(value) => handleInputChange(field.name, value)}
                    onInsurerSelect={(insurer) => {
                      // Auto-fill related fields if they exist
                      const addressField = template.fields.find(f => 
                        f.name.toLowerCase().includes('morada') && f.name.toLowerCase().includes('seguradora')
                      )
                      const nifField = template.fields.find(f => 
                        (f.name.toLowerCase().includes('nif') || f.name.toLowerCase().includes('nipc')) && 
                        f.name.toLowerCase().includes('seguradora')
                      )
                      if (addressField && insurer.address) {
                        handleInputChange(addressField.name, insurer.address)
                      }
                      if (nifField && insurer.nif) {
                        handleInputChange(nifField.name, insurer.nif)
                      }
                    }}
                    placeholder={field.placeholder || field.label}
                    required={field.required}
                  />
                ) : (
                  <input
                    type={field.type}
                    id={field.name}
                    value={formData[field.name] || ''}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    className="input"
                    placeholder={field.placeholder || field.label}
                    required={field.required}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="card p-6 space-y-4">
            <h2 className="font-semibold text-gray-900">Assinatura</h2>

            <div className="flex rounded-xl bg-gray-100 p-1">
              <button
                type="button"
                onClick={() => setSignatureMode('draw')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  signatureMode === 'draw'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-600'
                }`}
              >
                <Pen className="w-4 h-4" />
                Desenhar
              </button>
              <button
                type="button"
                onClick={() => setSignatureMode('photo')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  signatureMode === 'photo'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-600'
                }`}
              >
                <Camera className="w-4 h-4" />
                Fotografar
              </button>
            </div>

            {signatureMode === 'draw' ? (
              <SignatureCanvas onSignatureChange={handleCanvasSignature} />
            ) : (
              <SignatureUpload onSignatureChange={handlePhotoSignature} />
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-700">PDF gerado com sucesso!</p>
            </div>
          )}

          <div className="space-y-3">
            <button
              type="button"
              onClick={handleGeneratePdf}
              disabled={!isFormValid() || generating}
              className="btn-primary w-full gap-2"
            >
              {generating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  A gerar...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Gerar e Descarregar PDF
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => setShowCloudWarning(true)}
              disabled={!isFormValid() || generating || uploading}
              className="btn-secondary w-full gap-2"
            >
              <Cloud className="w-5 h-5" />
              Guardar na Cloud (opcional)
            </button>

            <button
              type="button"
              onClick={handleGenerateLink}
              className="btn-secondary w-full gap-2"
            >
              <LinkIcon className="w-5 h-5" />
              Gerar Link Pré-preenchido
            </button>
          </div>

          <div className="flex justify-center">
            <Turnstile
              ref={turnstileRef}
              siteKey={TURNSTILE_SITE_KEY}
              onSuccess={handleTurnstileSuccess}
              onError={() => setTurnstileToken(null)}
              onExpire={handleTurnstileExpire}
              options={{
                theme: 'light',
                size: 'normal',
                refreshExpired: 'auto',
              }}
            />
          </div>

          <p className="text-xs text-center text-gray-500">
            Os seus dados são processados localmente no seu dispositivo e nunca são enviados para servidores externos.
          </p>
        </form>

        {showCloudWarning && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Aviso de Privacidade</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Ao guardar na cloud, o seu documento será enviado para os nossos servidores.
                  </p>
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-yellow-800">
                <ul className="space-y-2">
                  <li>• O documento ficará acessível através de um link único</li>
                  <li>• <strong>O ficheiro será automaticamente apagado após 30 dias</strong></li>
                  <li>• Qualquer pessoa com o link poderá aceder ao documento</li>
                </ul>
              </div>

              <p className="text-xs text-gray-500">
                Se preferir manter total privacidade, use apenas a opção "Gerar e Descarregar PDF".
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCloudWarning(false)}
                  className="btn-secondary flex-1"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCloudSave}
                  disabled={uploading}
                  className="btn-primary flex-1 gap-2"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      A enviar...
                    </>
                  ) : (
                    'Confirmar e Enviar'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {cloudUrl && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Documento Guardado!</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    O seu documento foi guardado na cloud com sucesso.
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-2">Link do documento:</p>
                <code className="text-sm text-gray-700 break-all">{cloudUrl}</code>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-xs text-yellow-800">
                <strong>Atenção:</strong> Este link expira em 30 dias. Após esse período, o ficheiro será automaticamente apagado.
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setCloudUrl(null)}
                  className="btn-secondary flex-1"
                >
                  Fechar
                </button>
                <button
                  onClick={handleCopyUrl}
                  className="btn-primary flex-1 gap-2"
                >
                  {copied ? (
                    <>
                      <Check className="w-5 h-5" />
                      Copiado!
                    </>
                  ) : (
                    <>
                      <Copy className="w-5 h-5" />
                      Copiar Link
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {showLinkModal && generatedLink && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <LinkIcon className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Link Gerado!</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Partilhe este link para pré-preencher o formulário.
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-2">Link pré-preenchido:</p>
                <code className="text-sm text-gray-700 break-all">{generatedLink}</code>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-800">
                <strong>Nota:</strong> Este link contém os dados preenchidos codificados. Os dados nunca são enviados para servidores - ficam apenas no URL.
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowLinkModal(false)}
                  className="btn-secondary flex-1"
                >
                  Fechar
                </button>
                <button
                  onClick={handleCopyLink}
                  className="btn-primary flex-1 gap-2"
                >
                  {linkCopied ? (
                    <>
                      <Check className="w-5 h-5" />
                      Copiado!
                    </>
                  ) : (
                    <>
                      <Copy className="w-5 h-5" />
                      Copiar Link
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
